export const HEALTH_PLAN_CARD_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes

export const HEALTH_PLAN_CARD_UPLOAD_SUPPORTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'application/pdf',
] as const

const HEALTH_PLAN_CARD_UPLOAD_SUPPORTED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.pdf'] as const

export enum FileValidationErrorCode {
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  NO_FILE_PROVIDED = 'NO_FILE_PROVIDED',
}

export interface FileValidationError {
  code: FileValidationErrorCode
}

export interface FileValidationResult {
  isValid: boolean
  error?: FileValidationError
}

/**
 * Ensures file size and file type are valid for health plan card upload
 */
export const validateHealthPlanCardFile = (file: File | null | undefined): FileValidationResult => {
  if (!file) {
    return {
      isValid: false,
      error: {
        code: FileValidationErrorCode.NO_FILE_PROVIDED,
      },
    }
  }

  if (file.size > HEALTH_PLAN_CARD_UPLOAD_MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: {
        code: FileValidationErrorCode.FILE_TOO_LARGE,
      },
    }
  }

  const isValidType = HEALTH_PLAN_CARD_UPLOAD_SUPPORTED_FILE_TYPES.includes(
    file.type as (typeof HEALTH_PLAN_CARD_UPLOAD_SUPPORTED_FILE_TYPES)[number],
  )

  // Check file extension as a fallback
  const fileName = file.name.toLowerCase()
  const hasValidExtension = HEALTH_PLAN_CARD_UPLOAD_SUPPORTED_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))

  if (!isValidType && !hasValidExtension) {
    return {
      isValid: false,
      error: {
        code: FileValidationErrorCode.UNSUPPORTED_FILE_TYPE,
      },
    }
  }

  return {
    isValid: true,
  }
}

export const areNamesMismatched = (name1: string, name2: string): boolean => {
  const trimmedName1 = name1.trim().toLowerCase()
  const trimmedName2 = name2.trim().toLowerCase()
  return trimmedName1.localeCompare(trimmedName2, 'en', { sensitivity: 'base' }) !== 0
}
